import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Countdown confirmation dialog — user can only confirm AFTER the
 * countdown finishes. Click outside or Cancel aborts safely.
 *
 * Usage:
 *   <ConfirmDeleteDialog
 *     open={open}
 *     title="Delete 'Alice in Wonderland'?"
 *     description="This cannot be undone…"
 *     confirmLabel="Delete"
 *     countdownSeconds={5}
 *     onCancel={() => setOpen(false)}
 *     onConfirm={async () => await doDelete()}
 *   />
 */
const ConfirmDeleteDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  countdownSeconds = 5,
  onConfirm,
  onCancel,
  testId = 'confirm-delete-dialog',
}) => {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemaining(countdownSeconds);
    setBusy(false);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, countdownSeconds]);

  if (!open) return null;

  const locked = remaining > 0 || busy;
  const progressPct = (1 - remaining / countdownSeconds) * 100;

  const handleConfirm = async () => {
    if (locked) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      // parent is expected to close dialog; fail-safe reset
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onCancel?.()}
      data-testid={testId}
    >
      <div
        className="bg-slate-900 border border-red-700/60 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-red-800/40 bg-gradient-to-r from-red-950/40 to-rose-950/40">
          <div className="w-9 h-9 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-white font-bold text-base truncate"
              data-testid={`${testId}-title`}
            >
              {title}
            </h3>
            <p className="text-xs text-red-300/70">Destructive action</p>
          </div>
          <button
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            className="w-8 h-8 rounded-full bg-slate-800/70 hover:bg-slate-700 text-gray-300 flex items-center justify-center disabled:opacity-50"
            aria-label="Close"
            data-testid={`${testId}-close`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          {description && (
            <p
              className="text-sm text-gray-300 leading-relaxed"
              data-testid={`${testId}-description`}
            >
              {description}
            </p>
          )}

          {/* Countdown progress bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                {remaining > 0 ? 'Hold on…' : 'Ready'}
              </span>
              <span
                className="text-xs font-mono text-red-300 font-semibold"
                data-testid={`${testId}-countdown`}
              >
                {remaining > 0 ? `${remaining}s` : 'confirm now'}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-500 to-rose-500 transition-all duration-1000 ease-linear"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-5 pt-0">
          <Button
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            variant="outline"
            className="flex-1 bg-transparent border-slate-600 text-gray-200 hover:bg-slate-800"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={locked}
            className={`flex-1 transition-all ${
              locked
                ? 'bg-red-900/40 text-red-300/60 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/30'
            }`}
            data-testid={`${testId}-confirm`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : remaining > 0 ? (
              <>
                {confirmLabel} in {remaining}s
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteDialog;
